/**
 * Session primitives with no dependencies.
 *
 * Kept separate from lib/auth.ts because the middleware runs in the Edge
 * runtime and importing the Supabase client there pulls in Node APIs it cannot
 * use. This module is safe everywhere.
 */

export const SESSION_COOKIE = 'shopace_demo_session';

/**
 * Mirrors the `roles` table. `manager`, `b2b` and `b2c` were added on
 * 26 August 2026 and this union did not follow, so a manager was cast to a
 * member that does not exist, fell through to 'customer', and saw none of the
 * twelve modules their matrix row grants (R-38).
 *
 * Widening the type makes it honest. It does NOT fix the authorisation:
 * `isAdmin()` still answers a question the matrix should answer. T024b replaces
 * it with `has_perm()`.
 */
export type Role = 'customer' | 'staff' | 'admin' | 'manager' | 'b2b' | 'b2c';

/**
 * FIXTURES MODE ONLY. With Supabase configured, `user_roles` is the authority
 * and this list is never consulted. The session route refuses to run at all
 * when NODE_ENV is production.
 *
 * These addresses are deliberately NOT plausible production addresses.
 *
 * They were briefly `admin@obsessedgymwear.com.au` and `guy@...`, changed while
 * removing the previous client's brand. That was a mistake: it made a fail-open
 * backdoor guessable on the client's own domain, which is worse than the
 * irrelevant domain it replaced. A non-routable address cannot be guessed into.
 *
 * Spec 003 FR-507: no address is granted a role by a hardcoded list. FR-506:
 * authentication fails closed. Tasks T024c and T024d delete this entirely.
 */
const ADMIN_EMAILS = ['dev-admin@localhost.invalid'];

/**
 * The address the sign-in walkthrough tells people to use.
 *
 * Exported rather than repeated in the UI: the hint and the list drifted apart
 * within one tick of each other, and the screen told users an address that had
 * just been disabled. Derive, do not copy. Same rule as FR-513.
 */
export const DEMO_ADMIN_EMAIL = ADMIN_EMAILS[0]!;

export function roleForEmail(email: string): Role {
  return ADMIN_EMAILS.includes(email.trim().toLowerCase()) ? 'admin' : 'customer';
}

export function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? 'there';
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase() + p.slice(1))
    .join(' ');
}
